import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Sparkles, PenTool, Monitor } from 'lucide-react';
import { MaskLine, FadeUp, EASE } from '@/components/Reveal';
import { Marquee } from '@/components/Marquee';
import { SERVICES, BILINGUAL, WHY_US, PROCESS, TOOLS, TEAM_ROLES } from '@/data/content';

export default function Services() {
  return (
    <main data-testid="services-page">
      {/* HERO */}
      <section className="px-6 sm:px-12 pt-40 pb-24 sm:pb-32">
        <div className="max-w-[1500px] mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
            className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-8"
          >
            The 360° Manifesto
          </motion.p>
          <h1 data-testid="services-headline" className="font-slab font-black leading-[0.98] tracking-tight text-[11vw] sm:text-[8.5vw] lg:text-[6.8vw]">
            <MaskLine delay={0.2}>HIGH-IMPACT</MaskLine>
            <span className="relative inline-block">
              <MaskLine delay={0.34}>CONTENT,</MaskLine>
              <motion.span
                initial={{ opacity: 0, y: 20, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -4 }}
                transition={{ delay: 0.95, duration: 0.7, ease: EASE }}
                className="font-script text-royal absolute -top-[0.6em] right-[-0.4em] text-[0.7em] pointer-events-none select-none"
              >
                rich
              </motion.span>
            </span>
            <MaskLine delay={0.48}>EVERY FORMAT.</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
            className="mt-9 max-w-xl text-base sm:text-lg leading-relaxed text-ink/75"
          >
            Strategy, content, production, design and management — under one roof, in two languages.
          </motion.p>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="services-chapters" className="border-b border-ink/15">
        {SERVICES.map((s, i) => (
          <FadeUp key={s.num}>
            <div
              data-testid={`chapter-${s.num}`}
              className="group grid md:grid-cols-12 gap-6 md:gap-10 items-start px-6 sm:px-12 py-14 sm:py-20 border-t border-ink/15 max-w-[1500px] mx-auto"
            >
              <div className="md:col-span-3">
                <span className="font-slab font-black text-7xl sm:text-8xl text-outline group-hover:text-butter group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                  {s.num}
                </span>
              </div>
              <div className="md:col-span-5">
                <h2 className="font-slab font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">{s.title}</h2>
                <p className="font-script text-royal text-3xl mt-2">{s.tagline}</p>
                <p className="mt-5 text-ink/75 leading-relaxed">{s.blurb}</p>
              </div>
              <div className="md:col-span-4 flex flex-wrap gap-2 md:justify-end content-start">
                {s.items.map((item) => (
                  <span key={item} className="text-xs font-medium border border-ink/25 rounded-full px-4 py-2 text-ink/75 group-hover:bg-butter group-hover:border-ink transition-colors duration-300">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </FadeUp>
        ))}
      </section>

      {/* BILINGUAL */}
      <section data-testid="bilingual-block" className="mx-3 sm:mx-6 my-10 bg-royal text-cream rounded-[2.5rem] px-6 sm:px-12 py-20 sm:py-28 overflow-hidden relative">
        <div className="max-w-[1500px] mx-auto">
          <FadeUp>
            <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-butter font-bold mb-5">Fluent in two languages</p>
                <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                  {BILINGUAL.title}
                </h2>
              </div>
              <p className="font-slab font-black text-6xl sm:text-8xl text-outline-cream select-none">EN ⇄ AR</p>
            </div>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-10">
            {BILINGUAL.blocks.map((b, i) => (
              <FadeUp key={b.name} delay={i * 0.1}>
                <div className="border-t border-cream/25 pt-7">
                  <h3 className="font-slab font-bold text-2xl sm:text-3xl mb-4">{b.name}</h3>
                  <p className="text-cream/80 leading-relaxed">{b.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-us" className="px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-[1500px] mx-auto">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-5">Why Butter</p>
            <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-16">
              Four reasons we spread well.
            </h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 gap-6">
            {WHY_US.map((w, i) => (
              <FadeUp key={w.num} delay={i * 0.07}>
                <div data-testid={`why-us-${w.num}`} className="group h-full bg-parchment border border-ink/15 rounded-2xl p-8 sm:p-10 hover:bg-butter hover:border-ink hover:-translate-y-1.5 transition-all duration-300">
                  <span className="font-slab font-black text-royal text-lg">{w.num}</span>
                  <h3 className="font-slab font-bold text-2xl sm:text-3xl mt-4 mb-4 tracking-tight">{w.title}</h3>
                  <p className="text-ink/70 leading-relaxed">{w.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="bg-ink text-cream mx-3 sm:mx-6 rounded-[2.5rem] px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-[1500px] mx-auto">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-butter font-bold mb-5">Our Process</p>
            <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-16">
              From brief to butter.
            </h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
            {PROCESS.map((p, i) => (
              <FadeUp key={p.num} delay={i * 0.08}>
                <div data-testid={`process-step-${p.num}`} className="border-t-2 border-butter pt-6">
                  <span className="font-slab font-black text-butter/50 text-4xl">{p.num}</span>
                  <h3 className="font-slab font-bold text-xl mt-4 mb-3 leading-snug">{p.title}</h3>
                  <p className="text-cream/65 text-sm leading-relaxed">{p.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS */}
      <section data-testid="tools-section" className="px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-[1500px] mx-auto">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-5">Our Tools</p>
            <h2 className="font-slab font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-16">
              Sharp knives, smooth butter.
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6">
            <FadeUp>
              <div data-testid="tools-ai" className="h-full bg-ink text-cream rounded-2xl p-8 sm:p-10">
                <Sparkles size={26} className="text-butter mb-6" />
                <h3 className="font-slab font-bold text-2xl mb-5 tracking-tight">{TOOLS.ai.title}</h3>
                <ul className="space-y-3.5 text-sm text-cream/75 leading-relaxed">
                  {TOOLS.ai.points.map((p) => (
                    <li key={p} className="flex gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-butter mt-2 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div data-testid="tools-design" className="h-full bg-parchment border border-ink/15 rounded-2xl p-8 sm:p-10">
                <PenTool size={26} className="text-royal mb-6" />
                <h3 className="font-slab font-bold text-2xl mb-3 tracking-tight">Design & Edit Suite</h3>
                <p className="text-sm text-ink/65 leading-relaxed mb-5">The kit behind every static, carousel, reel and film we deliver.</p>
                <div className="flex flex-wrap gap-2">
                  {TOOLS.design.map((t) => (
                    <span key={t} className="text-xs font-medium border border-ink/25 rounded-full px-4 py-2 text-ink/75">{t}</span>
                  ))}
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div data-testid="tools-cms" className="h-full bg-parchment border border-ink/15 rounded-2xl p-8 sm:p-10">
                <Monitor size={26} className="text-royal mb-6" />
                <h3 className="font-slab font-bold text-2xl mb-3 tracking-tight">CMS Expertise</h3>
                <p className="text-sm text-ink/65 leading-relaxed mb-5">Fluent in the platforms your site runs on — we write, format and publish content straight into your CMS, ready to go live.</p>
                <div className="flex flex-wrap gap-2">
                  {TOOLS.cms.map((t) => (
                    <span key={t} className="text-xs font-medium border border-ink/25 rounded-full px-4 py-2 text-ink/75">{t}</span>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TEAM MARQUEE */}
      <Marquee
        items={TEAM_ROLES}
        reverse
        testid="team-marquee"
        duration={36}
        className="bg-butter border-y border-ink py-4 font-slab font-bold uppercase tracking-wider text-xl sm:text-2xl text-ink"
      />

      {/* CTA */}
      <section className="px-6 sm:px-12 pt-24">
        <FadeUp className="max-w-[1500px] mx-auto">
          <Link
            to="/contact"
            data-testid="services-cta-banner"
            className="group flex flex-wrap items-center justify-between gap-8 bg-butter border border-ink rounded-[2rem] px-8 py-12 sm:p-16 shadow-[8px_8px_0_#1A1A1A] hover:shadow-none hover:translate-x-2 hover:translate-y-2 transition-all duration-300"
          >
            <h2 className="font-slab font-black text-3xl sm:text-5xl tracking-tight leading-tight">
              Ready when<br />you are.
            </h2>
            <span className="inline-flex items-center gap-3 font-slab font-bold text-lg bg-ink text-cream px-8 py-4 rounded-full">
              Start a Project
              <ArrowUpRight size={20} className="group-hover:rotate-45 transition-transform duration-300" />
            </span>
          </Link>
        </FadeUp>
      </section>
    </main>
  );
}
